import { CodeWidgetAnalysis, findingsWatchedBy, RiskId } from '../widgets/CodeWidget/analysis/code-widget-analysis'

/**
 * A request from the app summary to show one finding in one widget, under the risk it was clicked in.
 *
 * `nonce` is what makes clicking the same link twice work: the effect that acts on this fires on identity,
 * and two clicks on the same finding would otherwise be the same value.
 */
export type FocusRequest = { index: number; findingId: string | null; riskId: RiskId | null; nonce: number }

/** Findings that answer no named risk sit under "Other observations". */
export const OTHER_OBSERVATIONS = 'other'

/** A finding is rendered once per risk it answers, so its row is found by both. */
export function findingKey(riskId: RiskId | typeof OTHER_OBSERVATIONS | null, findingId: string): string {
  return `${riskId ?? OTHER_OBSERVATIONS}:${findingId}`
}

/** The first row a finding has in a widget's report: under the first risk that watches it, or under the others. */
export function firstFindingKey(widget: CodeWidgetAnalysis, findingId: string): string {
  const risk = widget.risks.find((candidate) => findingsWatchedBy(candidate.id).some((id) => id === findingId))
  return findingKey(risk?.id ?? OTHER_OBSERVATIONS, findingId)
}

/** Widgets carry no name of their own half the time, so number them — "untitled" six times over names nothing. */
export function widgetLabel(widget: CodeWidgetAnalysis, index: number): string {
  return widget.title ?? widget.widgetId ?? `Widget ${index + 1}`
}

export const widgetDomId = (index: number) => `cw-widget-${index}`

/** Opens every `<details>` around `element`, so scrolling to it lands on it rather than near a closed accordion. */
export function openAncestorDetails(element: Element | null) {
  for (let node = element?.closest('details'); node; node = node.parentElement?.closest('details') ?? null) {
    node.open = true
  }
}
